import { memo, useCallback, useEffect, useRef, useState } from 'react'
import './expandableList.scss'
import { cn } from '@quickmd-llc/design-system'

interface Props {
  items: string[]
  filterOptions: {
    states: string
    visitTypes: string
  }
  visitTypes: { VisitTypeName: string; StateCodes: string[] }[]
  type: 'states' | 'visits'
}

// The measurement pass stores a per-item `show` flag directly on the rendered
// element, so item refs are DOM nodes augmented with that optional flag.
type ItemRef = HTMLDivElement & { show?: boolean }

export const ExpandableList = memo(({ items, filterOptions, visitTypes, type }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const remainingRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(ItemRef | null)[]>([])
  const nextIndex = useRef(0)
  const [remaining, setRemaining] = useState({ count: 0, leftOffset: 0 })
  const [popupVisible, setPopupVisible] = useState(false)
  // When opened from the collapsed count badge (narrow viewports) the popup lists every item;
  // when opened from the "+N" overflow chip it lists only the items that didn't fit inline.
  const [popupShowAll, setPopupShowAll] = useState(false)
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 })
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current)
    }
  }, [])

  useEffect(() => {
    const containerEl = containerRef.current
    if (!containerEl) return

    const measure = () => {
      if (items.length === 0) return

      const containerWidth = containerEl.offsetWidth
      if (!containerWidth) return

      const GAP = 4
      const CHIP_RESERVED = 40

      // Pass 1: check if all items fit with no chip.
      let total = 0
      let allFit = true
      for (let i = 0; i < items.length; i++) {
        const item = itemRefs.current[i]
        if (item) {
          total += item.offsetWidth + GAP
          if (total > containerWidth) {
            allFit = false
            break
          }
        }
      }

      let count = 0
      if (allFit) {
        for (let i = 0; i < items.length; i++) {
          const item = itemRefs.current[i]
          if (item) item.show = true
        }
        count = items.length
      } else {
        // Pass 2: reserve space for the chip and fit as many items as possible.
        total = CHIP_RESERVED
        for (let i = 0; i < items.length; i++) {
          const item = itemRefs.current[i]
          if (item) {
            total += item.offsetWidth + GAP
            if (total > containerWidth) {
              item.show = false
            } else {
              item.show = true
              count++
            }
          }
        }
      }

      nextIndex.current = count + 1
      setRemaining({ count: items.length - count, leftOffset: 0 })
    }

    const ro = new ResizeObserver(measure)
    ro.observe(containerEl)
    measure()

    return () => ro.disconnect()
    // BULK SUPPRESSION: CRA -> VITE
  }, [items, filterOptions])

  useEffect(() => {
    if (items.length) {
      if (type === 'states') {
        // @ts-expect-error TS7034 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
        let relatedStates = []
        visitTypes.map((visit) => {
          if (filterOptions.visitTypes.includes(visit.VisitTypeName)) {
            // @ts-expect-error TS7005 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
            relatedStates = relatedStates.concat(visit.StateCodes)
          }
        })

        items.sort((a, b) => {
          // @ts-expect-error TS7005 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
          const aInFilter = relatedStates.includes(a) ? 0 : 1

          // @ts-expect-error TS7005 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
          const bInFilter = relatedStates.includes(b) ? 0 : 1
          return aInFilter - bInFilter
        })

        items.sort((a, b) => {
          const aInFilter = filterOptions.states.split(';').includes(a) ? 0 : 1
          const bInFilter = filterOptions.states.split(';').includes(b) ? 0 : 1
          return aInFilter - bInFilter
        })
      } else {
        // @ts-expect-error TS7034 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
        let relatedVisits = []
        visitTypes.map((visit) => {
          if (visit.StateCodes.includes(filterOptions.states)) {
            // @ts-expect-error TS7005 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
            relatedVisits = relatedVisits.concat(visit.VisitTypeName)
          }
        })

        items.sort((a, b) => {
          // @ts-expect-error TS7005 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
          const aInFilter = relatedVisits.includes(a) ? 0 : 1

          // @ts-expect-error TS7005 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
          const bInFilter = relatedVisits.includes(b) ? 0 : 1
          return aInFilter - bInFilter
        })
        items.sort((a, b) => {
          const aInFilter = filterOptions.visitTypes.split(';').includes(a) ? 0 : 1
          const bInFilter = filterOptions.visitTypes.split(';').includes(b) ? 0 : 1
          return aInFilter - bInFilter
        })
      }
    }
  }, [filterOptions, items, type, visitTypes])

  const getShading = useCallback(
    (item: string) => {
      if (type === 'states') {
        if (
          filterOptions.states
            .split(';')
            .map((state) => state.substring(state.lastIndexOf(' ')))
            .find((state) => state === item)
        )
          return 'main'
        const filteredVisitTypeNames = filterOptions.visitTypes.split(';')
        let secondary = ''
        visitTypes.map((visitType) => {
          if (
            visitType.StateCodes.find((state) => state === item) &&
            filteredVisitTypeNames.find((visitName) => visitName === visitType.VisitTypeName)
          ) {
            secondary = 'secondary'
          }
        })
        return secondary
      }
      if (filterOptions.visitTypes.split(';').find((visitTypeName) => visitTypeName === item)) return 'main'
      const filteredStates = filterOptions.states.split(';')
      let secondary = ''
      visitTypes.map((visitType) => {
        if (
          visitType.VisitTypeName === item &&
          filteredStates.filter((value) => visitType.StateCodes.includes(value)).length
        ) {
          secondary = 'secondary'
        }
      })
      return secondary
    },
    [filterOptions.states, filterOptions.visitTypes, type, visitTypes],
  )

  const showPopup = (e: React.MouseEvent<HTMLDivElement>, showAll = false) => {
    if (hideTimer.current) clearTimeout(hideTimer.current)
    const rect = e.currentTarget.getBoundingClientRect()
    setPopupPos({ top: rect.bottom + 4, left: rect.left })
    setPopupShowAll(showAll)
    setPopupVisible(true)
  }

  const scheduleHide = () => {
    hideTimer.current = setTimeout(() => setPopupVisible(false), 80)
  }

  const cancelHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current)
  }

  return (
    <div className='expandable-list-wrapper'>
      {/* Collapsed count badge — shown on narrow viewports (< xl) so Licenses/Services take
          minimal width and don't crowd out the Name column. Hover reveals every item, mirroring
          the "+N" overflow interaction. */}
      {items.length ?
        <div className='flex xl:hidden'>
          <div
            className='expandable-list-name expandable-list-overflow showing'
            role='button'
            tabIndex={0}
            onMouseEnter={(e) => showPopup(e, true)}
            onMouseLeave={scheduleHide}>
            {items.length}
          </div>
        </div>
      : null}

      {/* Full chip list — shown at xl and up. Hidden below xl via `max-xl:!hidden`; the bang beats
          the SCSS `display:flex` on `.expandable-list-container` regardless of stylesheet order. */}
      <div ref={containerRef} className='expandable-list-container max-xl:!hidden'>
        {items.map((item, index) => {
          const shading = getShading(item)
          return (
            <div
              className={cn('expandable-list-name', {
                'is-main': shading === 'main',
                'is-secondary': shading === 'secondary',
                showing: itemRefs.current[index]?.show,
              })}
              key={`${item}_${index}`}
              ref={(el) => {
                itemRefs.current[index] = el
              }}>
              {item}
            </div>
          )
        })}
        {remaining.count ?
          <div
            className={cn('expandable-list-name', 'expandable-list-overflow', {
              // @ts-expect-error TS2345 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
              'is-main': getShading(items[nextIndex.current]) === 'main',

              // @ts-expect-error TS2345 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
              'is-secondary': getShading(items[nextIndex.current]) === 'secondary',
              showing: true,
            })}
            ref={remainingRef}
            onMouseEnter={showPopup}
            onMouseLeave={scheduleHide}>
            +{remaining.count}
          </div>
        : null}
      </div>

      {popupVisible ?
        <div
          className='el-overflow-popup'
          style={{ position: 'fixed', top: popupPos.top, left: popupPos.left }}
          onMouseEnter={cancelHide}
          onMouseLeave={scheduleHide}>
          <div className='expandable-list-container el-overflow-popup__chips'>
            {items.map((item, index) => {
              const shading = getShading(item)
              return (
                <div
                  className={cn('expandable-list-name', {
                    'is-main': shading === 'main',
                    'is-secondary': shading === 'secondary',
                    showing: popupShowAll || !itemRefs.current[index]?.show,
                  })}
                  key={`${item}_${index}_2`}>
                  {item}
                </div>
              )
            })}
          </div>
        </div>
      : null}
    </div>
  )
})
